/**
 * Service da pagina Minhas Pendencias (REF-16).
 *
 * Endpoints backend (app `tarefas`):
 *   GET    /api/minhas-pendencias                  -> { tasks, stats }
 *   POST   /api/tarefas                            -> cria tarefa manual
 *   PATCH  /api/tarefas/{id}                       -> edita campos
 *   DELETE /api/tarefas/{id}
 *   POST   /api/tarefas/{id}/alternar-status       body {status?}
 *   POST   /api/tarefas/{id}/subtarefas            body {titulo, prazo?}
 *   POST   /api/tarefas/{id}/alternar-subtarefa    body {subtarefa_id}
 *   POST   /api/tarefas/{id}/comentar              body {texto}
 *   POST   /api/tarefas/{id}/arquivar              (reversivel)
 *   POST   /api/tarefas/{id}/desarquivar
 *   POST   /api/tarefas/{id}/atribuir              body {responsavel: id|null}
 *   GET    /api/tarefas/usuarios-atribuiveis       -> [{id, nome}]
 */

import { PHARUCUT_URL } from "../config/produtos";

export function getMinhasPendencias(request) {
  return request("/api/minhas-pendencias");
}

export function criarTarefa(request, dados) {
  return request("/api/tarefas", {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarTarefa(request, id, dados) {
  return request(`/api/tarefas/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarTarefa(request, id) {
  return request(`/api/tarefas/${id}`, { method: "DELETE" });
}

export function alternarStatusTarefa(request, id, status = null) {
  return request(`/api/tarefas/${id}/alternar-status`, {
    method: "POST",
    body: JSON.stringify(status ? { status } : {}),
  });
}

export function adicionarSubtarefa(request, id, { titulo, prazo = null }) {
  return request(`/api/tarefas/${id}/subtarefas`, {
    method: "POST",
    body: JSON.stringify({ titulo, prazo }),
  });
}

export function alternarSubtarefa(request, id, subtarefaId) {
  return request(`/api/tarefas/${id}/alternar-subtarefa`, {
    method: "POST",
    body: JSON.stringify({ subtarefa_id: subtarefaId }),
  });
}

export function comentarTarefa(request, id, texto) {
  return request(`/api/tarefas/${id}/comentar`, {
    method: "POST",
    body: JSON.stringify({ texto }),
  });
}

export function arquivarTarefa(request, id) {
  return request(`/api/tarefas/${id}/arquivar`, { method: "POST" });
}

export function desarquivarTarefa(request, id) {
  return request(`/api/tarefas/${id}/desarquivar`, { method: "POST" });
}

// responsavel: id de usuario ou null (remove a atribuicao).
export function atribuirTarefa(request, id, responsavel) {
  return request(`/api/tarefas/${id}/atribuir`, {
    method: "POST",
    body: JSON.stringify({ responsavel }),
  });
}

// Lista enxuta {id, nome} de usuarios ativos (endpoint proprio do app tarefas,
// sem exigir usuario:view — qualquer usuario autenticado pode atribuir).
export function listarUsuariosAtribuiveis(request) {
  return request("/api/tarefas/usuarios-atribuiveis");
}

// Grupos e rotulos (alinhados ao mockup) ------------------------------
export const GRUPOS = [
  { id: "atrasadas", label: "Atrasadas", tone: "danger" },
  { id: "hoje", label: "Vencem hoje", tone: "warning" },
  { id: "semana", label: "Esta semana", tone: "accent" },
  { id: "semprazo", label: "Sem prazo", tone: "neutral" },
];

export const PRIO_LABEL = { hi: "Alta", mid: "Média", low: "Baixa" };
export const STATUS_LABEL = { open: "A fazer", doing: "Em andamento", done: "Concluída" };
// token (mockup) -> valor interno aceito pelo backend (definir_status)
export const STATUS_INTERNO = {
  open: "A_FAZER",
  doing: "EM_ANDAMENTO",
  done: "CONCLUIDA",
};
export const STATUS_ORDEM = ["open", "doing", "done"];

// Mapa de origem -> rota do modulo (para drill-down de automaticas). Quando o
// id da pendencia identifica UM registro (LEAD-/OR-), vai direto nele.
const TAMANHO_UUID = 36;
// Do mais longo para o mais curto: "LEAD-" sozinho e o ultimo a testar.
const PREFIXOS_DE_LEAD = ["LEAD-VENDA-", "LEAD-NOVO-", "LEAD-TAREFA-", "LEAD-VISITA-"];

export function rotaOrigem(task) {
  if (task.origin !== "system") return null;
  switch (task.module) {
    case "Pos-venda":
      return "/posvenda/assistencias";
    case "Terceirizacao":
      return "/terceirizacao/oses";
    case "Comercial": {
      // A pendencia de follow-up nasce de UM lead (id "LEAD-<uuid>", ver
      // tarefas/services._coletar_followups_lead). Cair no pipeline inteiro
      // obrigava a pessoa a procurar o card entre dezenas: vai direto ao
      // cockpit. A revisao de orcamento ("OR-<uuid>") idem, para o orcamento.
      // A venda sem ganho no CRM (CRM-04) usa "LEAD-VENDA-<uuid>": o prefixo
      // mais longo vem antes, senao o uuid sai com "VENDA-" na frente.
      // CRM-25 / FORM-06: "LEAD-NOVO-<lead>", "LEAD-TAREFA-<lead>-<tarefa>" e
      // "LEAD-VISITA-<lead>-<compromisso>" — o uuid do lead vem logo apos o
      // prefixo (36 caracteres); o id do filho fica depois.
      const id = String(task.id || "");
      for (const prefixo of PREFIXOS_DE_LEAD) {
        if (id.startsWith(prefixo)) {
          return `/crm/leads/${id.slice(prefixo.length, prefixo.length + TAMANHO_UUID)}`;
        }
      }
      if (id.startsWith("LEAD-")) return `/crm/leads/${id.slice("LEAD-".length)}`;
      if (id.startsWith("OR-")) return `/orcamentos/${id.slice("OR-".length)}`;
      return "/crm";
    }
    case "Contratos":
      // M-PED-3: `/comercial/contratos` não existe (App.jsx só tem
      // configuracoes/painel/formularios/templates/parceiros). O link
      // levava ao catch-all, que joga no dashboard sem explicar nada. O
      // contrato pendente nasce de um orçamento aprovado, então é lá que
      // a pessoa resolve.
      return "/orcamentos";
    case "Estoque":
      // A pendência de consumo sem baixa (Fase 5) nasceu sem este case:
      // `rotaOrigem` devolvia null e o TaskModal não renderizava o botão
      // de abrir. O 3º passo do padrão de hub tinha sido esquecido.
      //
      // As duas pendências de Estoque se resolvem em telas diferentes, e o
      // prefixo do id diz qual é qual. "Cadastrar: <produto>" (PROD-) é
      // trabalho do almoxarife na fila de pendentes — mandá-lo para a
      // produção deixava o produto entrando nos orçamentos com custo zero.
      return String(task.id || "").startsWith("PROD-")
        ? "/estoque/pendentes"
        : "/producao";
    case "Logística": {
      // A pendencia de assinatura fora dos autorizados nasce de UMA entrega
      // ("OE-ASSINATURA-<uuid>", tarefas/services). O alerta e o botao de
      // conferir ficam na ficha da OE — a lista nao resolve nada.
      const id = String(task.id || "");
      const PREFIXO_ASSINATURA = "OE-ASSINATURA-";
      if (id.startsWith(PREFIXO_ASSINATURA)) {
        return `/logistica/entregas/${id.slice(PREFIXO_ASSINATURA.length)}`;
      }
      return "/logistica/entregas";
    }
    case "PharuCut":
      // A chapa faltando (ADR-0048 do Cut) se resolve no Terminal do
      // PharuCut, outro produto: o TaskModal abre endereço absoluto em
      // outra aba (o `navigate` do shell só conhece as rotas do ERP).
      return `${PHARUCUT_URL}/terminal`;
    case "Financeiro":
      return "/financeiro/contas-receber";
    case "Fiscal":
      return "/fiscal/notas-fiscais";
    case "Producao":
      // `/producao/ambientes` sem id não é rota: o shell só registra
      // `ambientes/:id` e o resto cai no catch-all, que devolve a pessoa
      // ao painel sem dizer por quê. O painel É a lista de ambientes.
      return "/producao";
    case "Mapa da Produção": {
      // A tarefa/peça atrasada leva ao setor dela (`link` =
      // "/legado/mapa/setor/<id>", tarefas/services._coletar_mapa_producao).
      const link = String(task.link || "");
      return link.startsWith("/legado/mapa") ? link : "/legado/mapa";
    }
    case "Área do PCP": {
      // Execução parada leva direto a ela (`link` =
      // "/legado/area-pcp/execucoes/<id>", tarefas/services._coletar_area_pcp).
      const link = String(task.link || "");
      return link.startsWith("/legado/area-pcp") ? link : "/legado/area-pcp/em-andamento";
    }
    default:
      return null;
  }
}
